/**
 * ClassYearBar — stacked horizontal bar with 3 buckets:
 *   - Graduating now   (SR + GR) — brand-primary green
 *   - Graduating soon  (JR)      — brand-accent ochre (85% opacity)
 *   - Underclassmen    (SO + FR) — warm tan #C7B7A0
 *
 * WI-36: accepts optional `highlightBucket` prop to visually emphasize the
 * cohort relevant to the currently-selected target year. Non-highlighted buckets
 * render at 0.3 opacity; the highlighted bucket renders at full opacity with a
 * 2px solid border in the bucket's color.
 *
 * Note on imprecision: SO (years_out=2) and FR (years_out=3) both map to the
 * "under" bucket visually. The distinction is made in scoring logic, not bucket
 * count — the bar cannot differentiate them.
 *
 * Matches detail-atoms.jsx ClassYearBar, using Tailwind tokens where possible.
 */
import React from 'react';
import type { ClassCounts } from '../../api/school';

export type HighlightBucket = 'now' | 'next' | 'under';

interface Props {
  counts: ClassCounts;
  /** Bar height in px (default 10). */
  height?: number;
  /** When true show compact "N now · N next · N under" legend. */
  compact?: boolean;
  /** When false (default true) show the legend below the bar. */
  showLegend?: boolean;
  /**
   * WI-36: which bucket to highlight as "graduating in target year".
   * - 'now'   → SR+GR (years_out=0)
   * - 'next'  → JR    (years_out=1)
   * - 'under' → SO+FR (years_out=2 or years_out=3)
   * - null / undefined → no highlight (existing behavior preserved)
   */
  highlightBucket?: HighlightBucket | null;
}

const COLOR_NOW   = '#1B5E42';   // brand.primary
const COLOR_SOON  = '#A0671E';   // brand.accent, 85% opacity applied via rgba
const COLOR_UNDER = '#C7B7A0';   // warm tan

const DIM_OPACITY = 0.3;

export function ClassYearBar({
  counts,
  height = 10,
  compact = false,
  showLegend = true,
  highlightBucket,
}: Props) {
  if (!counts) return null;
  const now   = (counts.SR ?? 0) + (counts.GR ?? 0);
  const soon  = counts.JR ?? 0;
  const under = (counts.SO ?? 0) + (counts.FR ?? 0);
  const total = now + soon + under;
  if (total === 0) return null;

  const pct = (n: number) => `${(n / total) * 100}%`;

  // Compute opacity for each bucket based on highlightBucket.
  // When highlightBucket is null/undefined all buckets are at full opacity.
  const opacityNow   = !highlightBucket || highlightBucket === 'now'   ? 1 : DIM_OPACITY;
  const opacitySoon  = !highlightBucket || highlightBucket === 'next'  ? 1 : DIM_OPACITY;
  const opacityUnder = !highlightBucket || highlightBucket === 'under' ? 1 : DIM_OPACITY;

  const borderNow   = highlightBucket === 'now'   ? `2px solid ${COLOR_NOW}`   : undefined;
  const borderSoon  = highlightBucket === 'next'  ? `2px solid ${COLOR_SOON}`  : undefined;
  const borderUnder = highlightBucket === 'under' ? `2px solid ${COLOR_UNDER}` : undefined;

  // Build aria-label with highlight context for screen readers.
  const ariaLabel = (() => {
    const base = `${now} graduating now, ${soon} next, ${under} underclassmen`;
    if (!highlightBucket) return base;
    const ctx =
      highlightBucket === 'now'   ? `${now} graduating in target year (SR+GR class)`  :
      highlightBucket === 'next'  ? `${soon} graduating in target year (JR class)`     :
      /* under */                   `${under} graduating in target year (SO+FR class)`;
    return `${ctx}, ${base}`;
  })();

  return (
    <div>
      {/* The bar itself */}
      <div
        className="flex w-full bg-surface-sunken rounded-sm overflow-hidden"
        style={{ height }}
        aria-label={ariaLabel}
        role="img"
      >
        {now > 0 && (
          <div
            title={`Graduating now: ${counts.SR} SR + ${counts.GR} GR`}
            style={{
              width: pct(now),
              background: COLOR_NOW,
              opacity: opacityNow,
              border: borderNow,
              boxSizing: 'border-box',
            }}
          />
        )}
        {soon > 0 && (
          <div
            title={`Graduating soon: ${counts.JR} JR`}
            style={{
              width: pct(soon),
              background: `rgba(160, 103, 30, 0.85)`,
              opacity: opacitySoon,
              border: borderSoon,
              boxSizing: 'border-box',
            }}
          />
        )}
        {under > 0 && (
          <div
            title={`Underclassmen: ${counts.SO} SO + ${counts.FR} FR`}
            style={{
              width: pct(under),
              background: COLOR_UNDER,
              opacity: opacityUnder,
              border: borderUnder,
              boxSizing: 'border-box',
            }}
          />
        )}
      </div>

      {/* Legend */}
      {showLegend && compact && (
        <div className="flex gap-2.5 mt-1 text-[10px] text-gray-500 tabular-nums">
          <span><span className="text-gray-900 font-semibold">{now}</span> now</span>
          <span><span className="text-gray-900 font-semibold">{soon}</span> next</span>
          <span><span className="text-gray-900 font-semibold">{under}</span> under</span>
        </div>
      )}
      {showLegend && !compact && (
        <div className="flex flex-wrap gap-3.5 mt-1.5">
          <SegLabel color={COLOR_NOW}   label="Graduating now"  n={now}   sub={counts.GR > 0 ? `${counts.SR} SR + ${counts.GR} GR` : undefined} />
          <SegLabel color="rgba(160,103,30,0.85)" label="Next up" n={soon} sub={counts.JR > 0 ? `${counts.JR} JR` : undefined} />
          <SegLabel color={COLOR_UNDER} label="Underclassmen"   n={under} sub={under > 0 ? `${counts.SO} SO + ${counts.FR} FR` : undefined} />
        </div>
      )}
    </div>
  );
}

function SegLabel({
  color,
  label,
  n,
  sub,
}: {
  color: string;
  label: string;
  n: number;
  sub?: string;
}) {
  return (
    <div className="flex items-baseline gap-1.5 min-w-0">
      <span
        style={{ background: color }}
        className="w-1.5 h-1.5 rounded-[1px] flex-shrink-0 inline-block self-center"
        aria-hidden="true"
      />
      <span className="text-[11px] font-semibold text-gray-900 tabular-nums">{n}</span>
      <span className="text-[10px] text-gray-500 truncate">
        {label}{sub ? ` (${sub})` : ''}
      </span>
    </div>
  );
}
